import React, { useState } from 'react';
import { AnimatePresence, motion, PanInfo } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { pageTypes } from '../data/drum';
import { pageSamples } from '../data/pageSamples';
const EASE = [0.23, 1, 0.32, 1] as const;
const SWIPE_THRESHOLD = 60;
const arrowClass = 'absolute top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-4 border-white bg-red-700 text-white shadow-lg shadow-red-700/40 transition-[transform,background-color] duration-150 ease-out hover:bg-red-800 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-red-700/40 sm:h-16 sm:w-16';
function pad(value: number): string {
  return value.toString().padStart(2, '0');
}
export function InsidePages() {
  const [[index, direction], setPage] = useState<[number, number]>([0, 0]);
  const total = pageSamples.length;

  // No pages yet: hide the section until the real Volume 1 pages are added to data/pageSamples.ts.
  if (total === 0) return null;
  const page = pageSamples[index];
  const paginate = (step: number) => {
    setPage([(index + step + total) % total, step]);
  };
  const goTo = (next: number) => {
    if (next === index) return;
    setPage([next, next > index ? 1 : -1]);
  };
  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (info.offset.x < -SWIPE_THRESHOLD) paginate(1);else if (info.offset.x > SWIPE_THRESHOLD) paginate(-1);
  };
  return <section id="inside-pages" className="w-full scroll-mt-12 border-y border-slate-900/10 bg-stone-100 px-4 py-16 sm:px-14 sm:py-28" data-id="element-188" data-mp="18vqyxnkaqiie">
      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-12 lg:items-center lg:gap-14" data-id="element-189" data-mp="uxj6nsy7g5dk">
        <div className="px-2 text-center sm:px-0 lg:col-span-4 lg:text-left" data-id="element-190" data-mp="yyl9skv8t1kt">
          <p className="font-serif text-2xl italic leading-none text-red-700" data-id="element-191" data-mp="2p9za6lzm95eq">Look inside Volume 1</p>
          <h2 className="mt-3 font-serif text-[2.3rem] leading-[1.02] tracking-tight text-slate-900 sm:text-6xl" data-id="element-192" data-mp="2t9zyqzgfiwzi">
            Real pages <em className="text-red-700" data-id="element-193" data-mp="138v4aew3p09">you can read.</em>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-900/75" data-id="element-194" data-mp="11xsidx6knaq">
            The studio year plan, lessons minute by minute with “1 e & a” counting charts, worksheets in 3 levels,
            lesson notes, tests and the answer key – exactly as they appear in Volume 1.
          </p>
          <p className="mt-8 hidden border-t border-slate-900/15 pt-5 text-sm text-slate-900/70 lg:block" data-id="element-195" data-mp="osbo2eez54a2">
            {pageTypes.join(' · ')}
          </p>
        </div>

        <div className="min-w-0 lg:col-span-8" data-id="element-196" data-mp="fzq8t8ajxfpz">
          <div role="group" aria-roledescription="carousel" aria-label="Real pages from Volume 1" tabIndex={0} onKeyDown={event => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            paginate(1);
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            paginate(-1);
          }
        }} className="mx-auto w-full max-w-xl rounded-2xl focus:outline-none focus-visible:ring-4 focus-visible:ring-red-700/30" data-id="element-197" data-mp="fyf4n7d725yc">
            <div className="relative px-2 sm:px-10" data-id="element-198" data-mp="1g2fwsg805mgb">
              <button type="button" onClick={() => paginate(-1)} aria-label="Previous page" className={`${arrowClass} left-0 sm:-left-2`} data-id="element-199" data-mp="1kyda0dnyycov">
                <ChevronLeftIcon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.75} aria-hidden="true" data-id="element-200" data-mp="1oyx8bkka3mcl" />
              </button>

              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg border border-slate-900/10 bg-white shadow-xl shadow-slate-900/15" data-id="element-201" data-mp="1yyxvxip6koml">
                <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-202" data-mp="23qpmht8c6ucq">
                  <motion.img key={page.src} src={page.src} alt={`${page.label} – ${page.title}`} custom={direction} variants={{
                  enter: (dir: number) => ({
                    x: dir >= 0 ? '40%' : '-40%',
                    opacity: 0
                  }),
                  center: {
                    x: 0,
                    opacity: 1
                  },
                  exit: (dir: number) => ({
                    x: dir >= 0 ? '-40%' : '40%',
                    opacity: 0
                  })
                }} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.24,
                  ease: EASE
                }} drag="x" dragConstraints={{
                  left: 0,
                  right: 0
                }} dragElastic={0.6} onDragEnd={handleDragEnd} draggable={false} className="absolute inset-0 block h-full w-full cursor-grab touch-pan-y select-none object-contain active:cursor-grabbing" data-id="element-203" data-mp="28npkdgv18qsr" />
                </AnimatePresence>
                <a href={page.src} target="_blank" rel="noopener noreferrer" aria-label={`Open ${page.label} full size`} className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-slate-900/80 px-3 py-1.5 text-xs font-semibold text-white transition-colors duration-150 ease-out hover:bg-slate-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-red-700/40" data-id="element-204" data-mp="1q1vysx1qy98p">
                  <ZoomInIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-205" data-mp="sazbl37wrswb" />
                  Full size
                </a>
              </div>

              <button type="button" onClick={() => paginate(1)} aria-label="Next page" className={`${arrowClass} right-0 sm:-right-2`} data-id="element-206" data-mp="j8pzwa0wlthc">
                <ChevronRightIcon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.75} aria-hidden="true" data-id="element-207" data-mp="16zvfqp6uglrn" />
              </button>
            </div>

            <div className="mt-6 flex items-center justify-center gap-4" data-id="element-208" data-mp="x8shrw97omwl">
              <p className="font-serif text-xl tabular-nums text-slate-900" aria-live="polite" data-id="element-209" data-mp="x8irs8o62fqw">
                {pad(index + 1)}
                <span className="text-slate-900/40" data-id="element-210" data-mp="1pxpyvw1osdnr"> / {pad(total)}</span>
              </p>
              <div className="flex gap-1" data-id="element-211" data-mp="1uo6b5ctrtt8j">
                {pageSamples.map((sample, dotIndex) => <button key={sample.src} type="button" onClick={() => goTo(dotIndex)} aria-label={`Go to page ${dotIndex + 1}`} aria-current={dotIndex === index} className="flex h-6 items-center px-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-700/40" data-id="element-212" data-mp="1uq9cuu4adat3" data-mpi={dotIndex}>
                    <span className={`block h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out ${dotIndex === index ? 'w-6 bg-red-700' : 'w-3 bg-slate-900/20'}`} data-id="element-213" data-mp="1ukearzr1dok5" />
                  </button>)}
              </div>
            </div>

            <div className="mt-4 min-h-[7.5rem] px-4 text-center sm:px-10" data-id="element-214" data-mp="1guu96x96yxcf">
              <p className="text-sm font-medium text-red-700" data-id="element-215" data-mp="1gspxht51wu1l">{page.label}</p>
              <h3 className="mt-1 font-serif text-2xl leading-tight text-slate-900" data-id="element-216" data-mp="1bvr4jdx4it4z">{page.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-900/70" data-id="element-217" data-mp="16y7rghc5wmem">{page.text}</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}